/**
 * Turn a `<CoordGeom>` into stationed polylines.
 *
 * Output is a list of *runs*: a new run starts whenever consecutive elements do
 * not share an endpoint (which happens with `<Chain>`, with survey files that
 * emit unordered `<Line>` elements, and with genuinely broken exports).
 */

import type { CgElement, CoordGeom, CurveEl, Pt, Pt2, SpiralEl } from '../landxml/types';
import { arcAt, arcSegments, solveArc, type ArcSolution } from './arc';
import { solveSpiral } from './spiral';

export interface CgVertex {
  x: number;
  y: number;
  z?: number;
  /** Internal station (distance along the geometry from the alignment start). */
  sta: number;
}

export interface CgSegmentInfo {
  index: number;
  kind: string;
  name?: string;
  staStart: number;
  staEnd: number;
  length: number;
  /** Index into the flattened vertex list. */
  from: number;
  to: number;
  radius?: number;
  deltaDeg?: number;
  method?: string;
  note?: string;
}

export interface CgTess {
  runs: CgVertex[][];
  segments: CgSegmentInfo[];
  totalLength: number;
  warnings: string[];
  /** True when every element joined end-to-end. */
  continuous: boolean;
}

export interface TessOptions {
  /** Chord-height tolerance for arcs and spirals, in model units. */
  tolerance?: number;
  staStart?: number;
  /** Resolves a `<Chain>` / `pntRef` name to a point. */
  lookup?: (name: string) => Pt2 | undefined;
}

const EPS_JOIN = 1e-6;

function xy(p: Pt | undefined): Pt2 | null {
  if (!p || p.x === undefined || p.y === undefined) return null;
  return p.z === undefined ? { x: p.x, y: p.y } : { x: p.x, y: p.y, z: p.z };
}

export function tessellateCoordGeom(cg: CoordGeom, opts: TessOptions = {}): CgTess {
  const tol = opts.tolerance ?? 0.01;
  const warnings: string[] = [];
  const warn = (m: string) => { if (warnings.length < 200) warnings.push(m); };

  const runs: CgVertex[][] = [];
  const segments: CgSegmentInfo[] = [];
  let cur: CgVertex[] = [];
  let sta = opts.staStart ?? 0;
  let flatIndex = 0;
  let continuous = true;

  function startRun(): void {
    if (cur.length > 1) runs.push(cur);
    else if (cur.length === 1) runs.push(cur);
    cur = [];
  }

  function lastPt(): CgVertex | undefined { return cur[cur.length - 1]; }

  /** Append a polyline for one element; `pts[0]` is the element start. */
  function emit(el: CgElement, i: number, pts: Array<[number, number]>, lengths: number[],
                z0?: number, z1?: number, extra?: Partial<CgSegmentInfo>): void {
    if (pts.length < 2) return;
    const total = lengths[lengths.length - 1];
    const prev = lastPt();
    const joins = prev && Math.hypot(prev.x - pts[0][0], prev.y - pts[0][1]) <= Math.max(EPS_JOIN, tol);
    if (prev && !joins) { continuous = false; startRun(); }
    // When this element joins the previous one they share a vertex, and that
    // shared vertex is this element's first: it already sits at `flatIndex - 1`.
    const from = cur.length ? flatIndex - 1 : flatIndex;
    const startSta = sta;
    for (let k = 0; k < pts.length; k++) {
      if (k === 0 && cur.length) continue; // shared vertex
      const s = startSta + lengths[k];
      const t = total > 0 ? lengths[k] / total : 0;
      let z: number | undefined;
      if (z0 !== undefined && z1 !== undefined) z = z0 + (z1 - z0) * t;
      else if (z0 !== undefined) z = z0;
      else if (z1 !== undefined) z = z1;
      cur.push({ x: pts[k][0], y: pts[k][1], z, sta: s });
      flatIndex++;
    }
    sta = startSta + total;
    segments.push({
      index: i, kind: el.kind, name: el.name,
      staStart: startSta, staEnd: sta, length: total,
      from, to: flatIndex - 1,
      ...extra,
    });
  }

  function straight(el: CgElement, i: number, a: Pt2, b: Pt2, note?: string): void {
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    emit(el, i, [[a.x, a.y], [b.x, b.y]], [0, len], a.z, b.z, { method: 'straight', note });
  }

  const els = cg.elements;
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    switch (el.kind) {
      case 'Line': {
        const a = xy(el.start) ?? resolve(el.start, opts);
        const b = xy(el.end) ?? resolve(el.end, opts);
        if (!a || !b) { warn(`Line #${i + 1} has an unusable Start/End; skipped`); continue; }
        straight(el, i, a, b);
        break;
      }
      case 'Curve': {
        const c = el as CurveEl;
        const a = xy(c.start) ?? resolve(c.start, opts);
        const b = xy(c.end) ?? resolve(c.end, opts);
        const sol: ArcSolution | null = solveArc(
          { ...c, start: a ?? c.start, end: b ?? c.end }, warn);
        if (!sol) {
          if (a && b) { straight(el, i, a, b, 'curve under-determined; drawn as a chord'); warn(`Curve #${i + 1} is under-determined; drawn as a chord`); }
          else warn(`Curve #${i + 1} has no usable geometry; skipped`);
          continue;
        }
        const n = arcSegments(sol, tol);
        const pts: Array<[number, number]> = new Array(n + 1);
        const lens: number[] = new Array(n + 1);
        for (let k = 0; k <= n; k++) {
          pts[k] = arcAt(sol, k / n);
          lens[k] = (sol.length * k) / n;
        }
        if (a) pts[0] = [a.x, a.y];
        if (b) pts[n] = [b.x, b.y];
        emit(el, i, pts, lens, a?.z, b?.z, {
          method: sol.method, radius: sol.r, deltaDeg: (sol.sweep * 180) / Math.PI,
        });
        break;
      }
      case 'Spiral': {
        const sp = el as SpiralEl;
        const a = xy(sp.start) ?? resolve(sp.start, opts);
        const b = xy(sp.end) ?? resolve(sp.end, opts);
        const sol = solveSpiral({ ...sp, start: a ?? sp.start, end: b ?? sp.end }, warn);
        if (!sol) {
          if (a && b) { straight(el, i, a, b, 'spiral under-determined; drawn as a chord'); warn(`Spiral #${i + 1} is under-determined; drawn as a chord`); }
          else warn(`Spiral #${i + 1} has no usable geometry; skipped`);
          continue;
        }
        const pts = sol.pts.slice();
        const lens = Array.from(sol.sta);
        if (a) pts[0] = [a.x, a.y];
        if (b) pts[pts.length - 1] = [b.x, b.y];
        emit(el, i, pts, lens, a?.z, b?.z, {
          method: sol.substituted ? `${sp.spiType} (as clothoid)` : sp.spiType,
          note: sol.closureError !== undefined && sol.closureError > Math.max(1e-3, 1e-5 * sol.length)
            ? `closure ${sol.closureError.toFixed(4)}` : undefined,
        });
        break;
      }
      case 'IrregularLine': {
        const list: Pt2[] = [];
        const a = xy(el.start) ?? resolve(el.start, opts);
        if (a) list.push(a);
        for (const p of el.pts) {
          const prev = list[list.length - 1];
          if (prev && Math.hypot(prev.x - p.x, prev.y - p.y) < EPS_JOIN) continue;
          list.push(p);
        }
        const b = xy(el.end) ?? resolve(el.end, opts);
        if (b) {
          const prev = list[list.length - 1];
          if (!prev || Math.hypot(prev.x - b.x, prev.y - b.y) > EPS_JOIN) list.push(b);
        }
        if (list.length < 2) { warn(`IrregularLine #${i + 1} has fewer than two points; skipped`); continue; }
        const pts: Array<[number, number]> = list.map((p) => [p.x, p.y]);
        const lens = [0];
        for (let k = 1; k < pts.length; k++) {
          lens.push(lens[k - 1] + Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]));
        }
        emit(el, i, pts, lens, list[0].z, list[list.length - 1].z, { method: 'polyline' });
        break;
      }
      case 'Chain': {
        const pts: Array<[number, number]> = [];
        let missing = 0;
        let z0: number | undefined; let z1: number | undefined;
        for (const nm of el.refs) {
          const p = opts.lookup?.(nm);
          if (!p) { missing++; continue; }
          pts.push([p.x, p.y]);
          if (z0 === undefined) z0 = p.z;
          z1 = p.z;
        }
        if (missing) warn(`Chain #${i + 1}: ${missing} of ${el.refs.length} point name(s) not found`);
        if (pts.length < 2) continue;
        const lens = [0];
        for (let k = 1; k < pts.length; k++) {
          lens.push(lens[k - 1] + Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]));
        }
        emit(el, i, pts, lens, z0, z1, { method: 'chain' });
        break;
      }
    }
  }
  startRun();
  return { runs, segments, totalLength: sta - (opts.staStart ?? 0), warnings, continuous };
}

function resolve(p: Pt | undefined, opts: TessOptions): Pt2 | null {
  if (!p?.pntRef || !opts.lookup) return null;
  return opts.lookup(p.pntRef) ?? null;
}

/** Total 2D length of a tessellation, useful for cross-checking `Alignment/@length`. */
export function tessLength(t: CgTess): number {
  let sum = 0;
  for (const r of t.runs) {
    for (let i = 1; i < r.length; i++) sum += Math.hypot(r[i].x - r[i - 1].x, r[i].y - r[i - 1].y);
  }
  return sum;
}
